import * as React from "react";
import { ActionType } from "../../../../../../react-core/Flux/ActionType";
import { BaseComponent } from "../../../../../../react-core/Html/BaseComponent";
import { Lightbox } from "../../../../../../react-core/Html/Components/Lightbox";
import TableFilterDropdown from "../../../../../../react-core/Html/Components/TableFilterDropdown";
import { PartLoading } from "../../../../../../react-core/Html/PartLoading";
import { ToggleButtonTwoOptions } from "../../../../../../react-core/Html/ToggleButtonTwoOptions";
import { JsonDateParser } from "../../../../../../react-core/Utils/JsonDateParser";
import { Objects } from "../../../../../../react-core/Utils/Objects";
import { ThreadType } from "../../../../../../Wrap/Constants/Enums/Domain/ThreadType";
import { ResultPageModel } from "../../../../../../Wrap/Core/SortingAndPaging/Models/ResultPageModel";
import { AlertDefinitionCategory } from "../../../../../../Wrap/Models/Domains/Threads/AlertDefinitionCategory";
import { NotificationConfiguration } from "../../../../../../Wrap/Models/Domains/Threads/NotificationConfiguration";
import { ThreadResponse } from "../../../../../../Wrap/Models/Domains/Threads/ThreadResponse";
import { ClientNotificationFiltersModel } from "../../../../../../Wrap/Models/Domains/Users/NotificationFilter/ClientNotificationFiltersModel";
import CoachMarker from "../../../../../Shared/CoachMarkers/CoachMarker";
import { Alert } from "../../../../../Shared/Components/Alert";
import AdviserSiteTagManager from "../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { LoadingSkeletonType } from "../../../../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeletonWrapper } from "../../../../../Shared/LoadingSkeleton/LoadingSkeletonWrapper";
import { UserStore } from "../../../../Stroes/Variable";
import { NotificationAction } from "../../Action/NotificationAction";
import { NotificationDateRangeTypeMap } from "../../Resource/Content";
import { NotificationFilterStore } from "../../Store/Variable";
import { NotificationConfigurations } from "./NotificationConfigurations";
import { NotificationFilter } from "./NotificationFilter";
import { NotificationsExplained } from "./NotificationsExplained";
import { NotificationTable } from "./NotificationTable";

interface INotificationProps {
    NotificationUpdate: () => void;
}

interface INotificationState {
    currentFilter: ClientNotificationFiltersModel;
    usingDefaultFilter: boolean;
    curPage: number;
    data: ResultPageModel<ThreadResponse>;
    showDeleteLightbox: boolean;
    deleteInstructionId: string;
    deleteActivityType: string;
    isLoading?: boolean;
    showNotificationConfiguration: boolean;
    toggleFilter: any;
    showNotificationsExplained: boolean;
    alertDefinitionCategories: AlertDefinitionCategory[];
}

export class Notification extends BaseComponent<INotificationProps, INotificationState> {
    constructor(props: any) {
        super(props);
        this.state = {
            currentFilter: null,
            usingDefaultFilter: true,
            curPage: 1,
            data: null,
            showDeleteLightbox: false,
            deleteInstructionId: null,
            deleteActivityType: null,
            isLoading: true,
            showNotificationConfiguration: false,
            toggleFilter: null,
            showNotificationsExplained: false,
            alertDefinitionCategories: [],
        };
        this.subscribeToStore(NotificationFilterStore, () => this.onPressFilter());
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    public componentDidMount() {
        this.defaultFilter();
        const i = window.location.pathname.lastIndexOf("/");
        const path = window.location.pathname.substr(i + 1);
        if (path == "configure-notification") {
            this.setState({ showNotificationConfiguration: true });
        }
        NotificationAction.getNotificationDefinitionCategories((result) => {
            this.setState({ alertDefinitionCategories: result });
        });
        this.addEvent({
            environment: {
                appType: "web",
                setting: "prod",
                timestamps: JsonDateParser.toJsonDateNoOffset(new Date()).toString(),
            },
            page: {
                pageUrl: window.location.pathname.includes("notification")
                    ? window.location.pathname
                    : window.location.pathname + "/notification",
                pageTitle: `${document.title} - dashboard - notification`,
            },
            user: {
                loginId: UserStore.getUserInfo()?.ClientId.toString(),
                internal: "yes",
            },
            event: "pageLoaded",
        });
    }

    private defaultFilter = () => {
        const defaultFilter = NotificationFilterStore.getDefaultFilter();
        this.setState({ usingDefaultFilter: true });
        this.searchNotification(defaultFilter, 1);
    };

    private onPressFilter = () => {
        if (NotificationFilterStore.getActionType() == ActionType.Update) {
            const id = NotificationFilterStore.getPressedId();
            const filter = NotificationFilterStore.getFilterItemById(id);
            this.setState({ currentFilter: filter, curPage: 1, usingDefaultFilter: id == null, isLoading: true });
            this.searchNotification(filter, 1);
        }
    };

    private onUpdateFilter(filter: ClientNotificationFiltersModel) {
        this.setState({ currentFilter: filter });
        this.searchNotification(filter, 1);
    }

    private searchNotification = (filter: ClientNotificationFiltersModel, curPage) => {
        const query = NotificationFilterStore.convertDashboardFilterToQuery(filter, curPage);
        this.setState({ isLoading: true, currentFilter: filter, curPage: curPage });
        NotificationAction.getNotificationTable(query, (d) => {
            this.setState({ data: d, isLoading: false });
        });
    };

    private updateThread = (thread: ThreadResponse) => {
        NotificationAction.updateThread(thread, (d) => {
            this.props.NotificationUpdate();
        });
        const updatedData = Objects.cloneObject(this.state.data);
        const index = updatedData?.Results?.findIndex((x) => x.Id == thread.Id);
        if (index >= 0) {
            updatedData.Results[index] = thread;
            this.setState({ data: updatedData });
        }
    };

    private updateConfigurations(configurations: Array<NotificationConfiguration>) {
        NotificationAction.updateNotificationConfiguration(configurations, () => {
            this.setState({ showNotificationConfiguration: false });
        });
    }

    private onClickFollowUp = () => {
        const filter = this.state.currentFilter;
        filter.FollowUp = !filter?.FollowUp;
        filter.IsRead = null;
        this.searchNotification(filter, 1);
    };

    private onClickActionRequired = () => {
        const filter = this.state.currentFilter;
        filter.EventType = filter?.EventType == ThreadType.Alert ? ThreadType.NotSpecified : ThreadType.Alert;
        this.searchNotification(filter, 1);
    };

    private renderNoResultWarningMessage = () => {
        const filter = this.state.currentFilter;
        return (
            <>
                {filter?.FollowUp ? (
                    <Alert
                        alertType={AlertType.Warning}
                        message={"There are currently no notifications to follow up."}
                    />
                ) : filter?.EventType == ThreadType.Alert ? (
                    <Alert
                        alertType={AlertType.Warning}
                        message={"There are currently no notifications requiring action."}
                    />
                ) : (
                    <Alert alertType={AlertType.Warning} message={"There are currently no notifications."} />
                )}
            </>
        );
    };

    private convertFilterToString(filter: ClientNotificationFiltersModel): string {
        return `${NotificationDateRangeTypeMap[filter?.DateRangeOption]}, Notification category: ${
            this.state.alertDefinitionCategories.find((ele) => ele.Id == filter?.EventCategoryId)?.Name ?? "All"
        }, ${filter?.IsRead == true ? "Read" : filter?.IsRead == false ? "Unread" : "All"}`;
    }

    public render(): JSX.Element {
        const { data, curPage, isLoading, currentFilter } = this.state;
        if (!data) {return <PartLoading isLoading />;}

        return (
            <>
                {this.state.showNotificationConfiguration && (
                    <Lightbox
                        show={this.state.showNotificationConfiguration}
                        title="Configure notifications"
                        onClose={() => this.setState({ showNotificationConfiguration: false })}
                        cssWrapper={"is-default-lightbox is-wide"}
                    >
                        <NotificationConfigurations
                            onCancel={() => this.setState({ showNotificationConfiguration: false })}
                            onApply={(changes) => {
                                this.updateConfigurations(changes);
                            }}
                        />
                    </Lightbox>
                )}
                <Alert
                    alertType={AlertType.Notification}
                    message={this.convertFilterToString(this.state.currentFilter)}
                    onAlertClose={() => {
                        NotificationAction.setPressedId(null);
                        this.defaultFilter();
                    }}
                    hideCloseButton={this.state.usingDefaultFilter}
                />
                <div className="row mt-37 mb-10">
                    <div className="col-auto col-middle">
                        <ToggleButtonTwoOptions
                            optionOne={currentFilter?.EventType == ThreadType.Alert ? "Action required off" : ""}
                            optionTwo={
                                currentFilter?.EventType == ThreadType.Alert
                                    ? "Action required only"
                                    : "All notifications"
                            }
                            cssWrapperFirstOption="option-one-label-sr-only"
                            value={currentFilter?.EventType == ThreadType.Alert}
                            onChange={(v) => this.onClickActionRequired()}
                        />
                    </div>

                    <div className="col-fluid">
                        <button className="follow-up-button" onClick={() => this.onClickFollowUp()}>
                            <span
                                className="icon"
                                aria-hidden={true}
                                aria-pressed={this.state.currentFilter.FollowUp ? true : false}
                            ></span>
                            <span className="text text-size-16">
                                {this.state.currentFilter.FollowUp ? <b>Follow up</b> : "Follow up"}
                            </span>
                        </button>
                    </div>
                    <div className="col-auto col-middle">
                        <button
                            type="button"
                            className="hyperlink"
                            onClick={() => this.setState({ showNotificationsExplained: true })}
                        >
                            Notifications explained
                        </button>
                    </div>
                    <div className="col-auto col-middle">
                        <CoachMarker coachMarkerKey="manage-notifications" />
                        <button
                            className="slider-button"
                            onClick={() => {
                                this.setState({ showNotificationConfiguration: true });
                            }}
                        >
                            <span className="icon" aria-hidden={true}></span>
                            <span className="text text-size-16">Configure notifications</span>
                        </button>
                    </div>
                    <div className="col-auto col-middle">
                        <CoachMarker coachMarkerKey="notifications-filter" />
                        <TableFilterDropdown
                            title="Notifications table filter"
                            cssWrapper="notification-filter"
                            onOpen={(e) => this.setState({ toggleFilter: e })}
                        >
                            <NotificationFilter
                                currentFilter={currentFilter}
                                onApply={(filter) => {
                                    this.onUpdateFilter(filter);
                                    this.state.toggleFilter(true);
                                }}
                                onCancel={() => {
                                    this.state.toggleFilter(true);
                                }}
                            />
                        </TableFilterDropdown>
                    </div>
                </div>
                <LoadingSkeletonWrapper isLoading={isLoading} skeletonType={LoadingSkeletonType.Chart}>
                    {data.Results?.length == 0 ? (
                        this.renderNoResultWarningMessage()
                    ) : (
                        <NotificationTable
                            data={data.Results}
                            needPagination={{
                                totalNumOfResult: data.Pager.TotalRecords,
                                onPaginationChange: (pageIndex, pageSize) => {
                                    this.setState({ curPage: pageIndex, isLoading: true });
                                    this.searchNotification(currentFilter, pageIndex);
                                },
                                currentPage: curPage,
                            }}
                            updateRow={(row) => this.updateThread(row)}
                        />
                    )}
                </LoadingSkeletonWrapper>
                {this.state.showNotificationsExplained && (
                    <Lightbox
                        title="Notifications explained"
                        show={this.state.showNotificationsExplained}
                        cssWrapper="is-default-lightbox"
                        onClose={() => this.setState({ showNotificationsExplained: false })}
                    >
                        <NotificationsExplained />
                    </Lightbox>
                )}
            </>
        );
    }
}
